import { supabase } from '@/integrations/supabase/client';

export interface AdminSettings {
  llm_temperature: number;
  enable_model_fallback: boolean;
  maintenance_mode: boolean;
}

export const DEFAULT_ADMIN_SETTINGS: AdminSettings = {
  llm_temperature: 0.2,
  enable_model_fallback: true,
  maintenance_mode: false,
};

class AdminSettingsService {
  private settings: AdminSettings = { ...DEFAULT_ADMIN_SETTINGS };
  private isInitialized = false;
  private listeners: ((settings: AdminSettings) => void)[] = [];

  async initialize() {
    if (this.isInitialized) return;
    
    try {
      await this.loadSettings();
      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize admin settings:', error);
      // Use defaults on error
      this.settings = { ...DEFAULT_ADMIN_SETTINGS };
      this.isInitialized = true;
    }
  }

  private async loadSettings() {
    const { data, error } = await supabase
      .from('settings')
      .select('key, value, is_enabled')
      .in('key', ['llm_temperature', 'enable_model_fallback', 'maintenance_mode']);

    if (error) throw error;

    const settingsMap = data?.reduce((acc, setting) => {
      if (setting.key === 'llm_temperature') {
        const raw = setting.value ?? null;
        acc.llm_temperature = raw !== null && raw !== undefined
          ? Number(raw)
          : DEFAULT_ADMIN_SETTINGS.llm_temperature;
      } else if (setting.key === 'enable_model_fallback') {
        acc.enable_model_fallback = setting.is_enabled ?? DEFAULT_ADMIN_SETTINGS.enable_model_fallback;
      } else if (setting.key === 'maintenance_mode') {
        acc.maintenance_mode = setting.is_enabled ?? DEFAULT_ADMIN_SETTINGS.maintenance_mode;
      }
      return acc;
    }, {} as Partial<AdminSettings>);

    this.settings = { ...DEFAULT_ADMIN_SETTINGS, ...settingsMap };
    this.notifyListeners();
  }

  async getSettings(): Promise<AdminSettings> {
    if (!this.isInitialized) {
      await this.initialize();
    }
    return { ...this.settings };
  }

  async getTemperature(): Promise<number> {
    if (!this.isInitialized) {
      await this.initialize();
    }
    return this.settings.llm_temperature;
  }

  async updateSettings(newSettings: Partial<AdminSettings>): Promise<void> {
    this.settings = { ...this.settings, ...newSettings };
    this.notifyListeners();
  }

  subscribe(listener: (settings: AdminSettings) => void): () => void {
    this.listeners.push(listener);
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  private notifyListeners() {
    this.listeners.forEach(listener => listener({ ...this.settings }));
  }

  // Refresh settings from database (useful for real-time updates)
  async refresh(): Promise<void> {
    try {
      await this.loadSettings();
    } catch (error) {
      console.error('Failed to refresh admin settings:', error);
    }
  }
}

// Export singleton instance
export const adminSettingsService = new AdminSettingsService();

// Helper function to get current LLM temperature
export const getCurrentLLMTemperature = async (): Promise<number> => {
  return await adminSettingsService.getTemperature();
}; 